import filesFromFolder from '../../api/getFilesFromFolder'
import getSignedURL from '../../api/getSignedURL'
import uploadFile from '../../api/uploadFile'
import parseJSON from '../../api/readJSONFile'
import deleteFile from '../../api/deleteFile'
import deleteFiles from '../../api/deleteFiles'
import uploadDirectoryToS3 from '../../api/uploadDirectory'
import uploadImage from '../../api/uploadImage'
import { getFileExtension, sortFiles } from '../../utils/fileHelpers'
import axios from 'axios'

const errorHandler = (message) => {
  return {
    message,
    code: 'error',
    displayModal: true,
  }
}

const successHandler = (message) => {
  return {
    message,
    code: 'success',
    displayModal: true,
  }
}

const fileToDelete = async (identifier) => {
  const files = await filesFromFolder(identifier)
  return files.find((file) => {
    return getFileExtension(file) === 'pdf'
  })
}

const state = {
  directory: {
    pressReleases: [],
    careers: [],
    videos: [],
    articles: [],
  },

  pressReleases: {
    S3Path: 'media/press-release/',
    directoryKey: 'pressReleases',
  },

  articles: {
    S3Path: 'media/articles/',
    directoryKey: 'articles',
  },

  careers: {
    S3Path: 'careers/',
    directoryKey: 'careers',
  },

  videos: {
    S3Path: 'media/videos/',
    directoryKey: 'videos',
  },

  file: {},

  feedback: {
    message: '',
    code: '',
    displayModal: false,
  },
}

const getters = {
  directory: (state) => state.directory,

  file: (state) => state.file,

  careersID: (state) => state.careers,

  pressReleasesID: (state) => state.pressReleases,

  articlesID: (state) => state.articles,

  videosID: (state) => state.videos,

  feedback: (state) => state.feedback,

  featured: (state) => (key) => {
    return state.directory[key].filter((data) => data.featured)
  },

  dataDeleted: (state) => (id, key) => {
    return !state.directory[key].includes(id)
  },
}

const mutations = {
  // SETTING DATA

  setDirectory(state, { directory, directoryKey: key }) {
    if (key === 'pressReleases' || key === 'careers') {
      directory = sortFiles(directory)
    }

    state.directory[key] = directory
  },

  setFile(state, file) {
    state.file = file
  },

  setFeedback(state, feedback) {
    state.feedback = feedback
  },

  // ADDING DATA

  addDataToDOM(state, { newIndex, key }) {
    state.directory[key].unshift(newIndex)
  },

  /**
   * @param {Object} state
   * @param {Object} file
   * compares inputted file date to dates in directory array
   * & inserts it accordingly into array
   * @returns {void}
   */
  addFileToDOM(state, { newIndex, key }) {
    let { date } = newIndex

    /**
     * @returns full text string
     * ex: "Wed Jun 02 2021 11:58:33 GMT-0400 (Atlantic Standard Time)"
     */
    date = new Date(date)

    const index = state.directory[key].findIndex((file) => {
      const compareDate = new Date(file.date)
      return date > compareDate
    })

    if (index === -1) {
      state.directory[key].push(newIndex)
    } else {
      state.directory[key].splice(index, 0, newIndex)
    }
  },

  // REMOVING DATA

  removeDataFromDOM(state, { identifier, key }) {
    return (state.directory[key] = state.directory[key].filter((data) => {
      return data.id !== identifier
    }))
  },

  // UPDATING DATA

  /**
   * @param {*} state
   * @param {Object} updateIndex and key
   * updates directory with new data
   */
  updateDirectoryInDOM(state, { updatedIndex, key }) {
    const { id } = updatedIndex

    const index = state.directory[key].findIndex((data) => {
      return data.id === id
    })

    state.directory[key][index] = updatedIndex

    if (key === 'pressReleases' || key === 'careers') {
      sortFiles(state.directory[key])
    }
  },
}

const actions = {
  // FETCHING DATA

  async fetchDirectory({ commit }, { S3Path, directoryKey }) {
    const indexPath = S3Path + 'index.json'

    try {
      const key = await filesFromFolder(indexPath, false)

      if (key.length >= 1) {
        let directory = await parseJSON(key[0])

        if (directoryKey === 'videos') {
          for (const [index, video] of directory.entries()) {
            const vimeoId = video.link.split('/').pop()
            const apiUrl = `https://vimeo.com/api/v2/video/${vimeoId}.json`

            const response = await axios.get(apiUrl)

            if (response.status === 200) {
              const { data } = response

              if (data[0] && data[0].thumbnail_large) {
                directory[index] = {
                  ...video,
                  image: data[0].thumbnail_large,
                }
                // return { ...video, image: data[0].thumbnail_large }
              }
            }
          }
        }

        console.log('directory:', directory)

        commit('setDirectory', { directory, directoryKey })
      }
    } catch (err) {
      console.warn(err)
      commit(
        'setFeedback',
        errorHandler('There was an issue fetching files, try again'),
      )
    }
  },

  /**
   *
   * @param {*} commit
   * @param {String} folder -> identifier
   * gets the keys for each file in the folder
   * then depending on what kind of file it is:
   * json -> gets data
   * pdf / image -> gets signedURL
   */
  async fetchFile({ commit }, folder) {
    try {
      const keys = await filesFromFolder(folder, true)
      const identifier = folder

      let pdfURL = ''
      let imageURL = ''
      let JSON = ''

      if (keys.length >= 1) {
        for (const key of keys) {
          const ext = getFileExtension(key)

          if (ext === 'json') {
            JSON = await parseJSON(key)
          } else if (ext === 'pdf') {
            pdfURL = await getSignedURL(key)
          } else {
            imageURL = await getSignedURL(key)
          }
        }
      }
      const file = { pdfURL, imageURL, JSON, identifier }
      commit('setFile', file)
    } catch (err) {
      commit(
        'setFeedback',
        errorHandler('There was an issue fetching the file, try again'),
      )
      console.warn(err)
    }
  },

  // ADD DATA

  /**
   *
   * @param {Object} param0
   * @param {Object} file
   * first uploads json data
   * if there is a pdf, then the pdf is added
   * then the directory (index.json) is updated
   */
  async addFileToS3({ commit, state, dispatch }, file) {
    const { fileJSON, identifier, newIndex, directoryKey: key } = file

    const oldDirectory = state.directory[key]

    const promises = [uploadFile(identifier, fileJSON)]

    if (fileJSON.file) {
      promises.push(uploadFile(identifier, fileJSON.file))
    }

    if (fileJSON.image) {
      const imageIdentifier = `${identifier}-${fileJSON.image.name}`
      promises.push(uploadImage(imageIdentifier, fileJSON.image))
    }

    try {
      // uploads the json / pdf & image (if there is one) in parallel
      await Promise.all(promises)

      // updates the dom & then uses that info to update directory in S3
      await commit('addFileToDOM', { newIndex, key })
      await uploadDirectoryToS3(state.directory[key], key)

      commit('setFeedback', successHandler('File added'))
    } catch (err) {
      console.warn(err)

      commit('setFeedback', errorHandler('File could not upload, try again.'))

      await deleteFiles(identifier)
      dispatch('resetDirectory', { oldDirectory, key })
    }
  },

  async addArticleToS3({ commit, state, dispatch }, data) {
    const { fileJSON, identifier, newIndex, directoryKey: key } = data

    const oldDirectory = state.directory[key]

    const promises = [uploadFile(identifier, fileJSON)]

    if (fileJSON.image) {
      const imageIdentifier = `${identifier}-${fileJSON.image.name}`
      promises.push(uploadImage(imageIdentifier, fileJSON.image))
    }

    try {
      await Promise.all(promises)
      await commit('addDataToDOM', { newIndex, key })
      await uploadDirectoryToS3(state.directory[key], key)

      commit('setFeedback', successHandler('Article added'))
    } catch (err) {
      console.warn(err)

      commit(
        'setFeedback',
        errorHandler('Article could not upload, try again.'),
      )

      await deleteFiles(identifier)
      dispatch('resetDirectory', { oldDirectory, key })
    }
  },

  async addVideoToS3({ commit, state, dispatch }, data) {
    const { newIndex, key } = data

    const oldDirectory = state.directory[key]

    try {
      await commit('addDataToDOM', { newIndex, key })
      await uploadDirectoryToS3(state.directory[key], key)

      commit('setFeedback', successHandler('Video added'))
    } catch (err) {
      console.warn(err)

      commit('setFeedback', errorHandler('Video could not upload, try again.'))

      dispatch('resetDirectory', { oldDirectory, key })
    }
  },

  // UPDATING DATA

  async updateFileInS3(
    { commit, dispatch },
    { file, updatedIndex, directoryKey: key },
  ) {
    let { JSON, identifier } = file

    const oldDirectory = state.directory[key]

    try {
      const promises = []

      if (JSON.file) {
        /**
         * if new pdf is added to be uploaded
         * removes url link if there is one
         * removes previously uploaded pdf if there is one
         * uploads new pdf
         */
        if (JSON.file.type) {
          if (JSON.link) {
            JSON.link = ''
          } else {
            await deleteFile(await fileToDelete(identifier))
          }

          // await uploadFile(identifier, JSON.file);
          promises.push(uploadFile(identifier, JSON.file))
        }

        /**
         * removes old pdf & uses new press release URL
         */
        if (JSON.link) {
          await deleteFile(await fileToDelete(identifier))
        }
      }

      if (JSON.image) {
        if (JSON.image.type) {
          const imageIdentifier = `${identifier}-${JSON.image.name}`
          promises.push(uploadImage(imageIdentifier, JSON.image))
        }
      }

      promises.push(uploadFile(identifier, JSON))

      await Promise.all(promises)

      /**
       * updates directory if the preview data has been edited
       */
      if (updatedIndex) {
        await commit('updateDirectoryInDOM', { updatedIndex, key })
        await uploadDirectoryToS3(state.directory[key], key)
      }

      commit('setFeedback', successHandler('File updated'))
    } catch (err) {
      console.warn(err)

      commit(
        'setFeedback',
        errorHandler('File could not be updated, try again.'),
      )

      dispatch('resetDirectory', { oldDirectory, key })
    }
  },

  async updateArticleInS3({ commit, state, dispatch }, data) {
    const { file, updatedIndex, directoryKey: key } = data
    const { JSON, identifier } = file

    const oldDirectory = state.directory[key]

    const promises = [uploadFile(identifier, file.JSON)]

    if (JSON.image) {
      if (JSON.image.type) {
        const imageIdentifier = `${identifier}-${JSON.image.name}`
        promises.push(uploadImage(imageIdentifier, JSON.image))
      }
    }

    try {
      await Promise.all(promises)

      if (updatedIndex) {
        await commit('updateDirectoryInDOM', { updatedIndex, key })
        await uploadDirectoryToS3(state.directory[key], key)
      }

      commit('setFeedback', successHandler('Article updated'))
    } catch (err) {
      console.warn(err)

      commit(
        'setFeedback',
        errorHandler('Article could not update, try again.'),
      )

      dispatch('resetDirectory', { oldDirectory, key })
    }
  },

  async updateVideoInS3({ commit, state, dispatch }, data) {
    const { updatedIndex, key } = data
    const oldDirectory = state.directory[key]

    try {
      await commit('updateDirectoryInDOM', { updatedIndex, key })
      await uploadDirectoryToS3(state.directory[key], key)

      commit('setFeedback', successHandler('Video updated'))
    } catch (err) {
      console.warn(err)

      commit('setFeedback', errorHandler('Video could not update'))

      dispatch('resetDirectory', { oldDirectory, key })
    }
  },

  // DELETING DATA

  async deleteFileFromS3({ commit, dispatch }, { identifier, key }) {
    try {
      await deleteFiles(identifier)
      await commit('removeDataFromDOM', { identifier, key })
      await uploadDirectoryToS3(state.directory[key], key)

      commit('setFeedback', successHandler('File deleted'))
    } catch (err) {
      console.warn(err)
      commit(
        'setFeedback',
        errorHandler('File could not be deleted, try again'),
      )
    }
  },

  async deleteVideoFromS3({ commit }, { identifier, key }) {
    try {
      await commit('removeDataFromDOM', { identifier, key })
      await uploadDirectoryToS3(state.directory[key], key)

      commit('setFeedback', successHandler('Video deleted'))
    } catch (err) {
      console.warn(err)

      commit('setFeedback', errorHandler('Video could not delete, try again'))
    }
  },

  async deleteArticleFromS3({ commit, state }, { identifier, key }) {
    try {
      await deleteFiles(identifier)
      await commit('removeDataFromDOM', {
        identifier,
        key,
      })
      await uploadDirectoryToS3(state.directory[key], key)

      commit('setFeedback', successHandler('Article deleted'))
    } catch (err) {
      console.warn(err)

      commit('setFeedback', errorHandler('Could not delete article, try again'))
    }
  },

  async resetDirectory({ commit }, { oldDirectory, key }) {
    await commit('setDirectory', {
      directory: oldDirectory,
      directoryKey: key,
    })
    await uploadDirectoryToS3(oldDirectory, key)
  },

  resetFeedback({ commit }) {
    commit('setFeedback', { message: '', code: '', displayModal: false })
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
